package link.erpango.quip.domain.entities

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.Transient
import kotlinx.serialization.json.Json
import link.erpango.quip.utils.DatetimeUtils

@Serializable
data class InitiateTransferRequest(
    @SerialName("target_user_id") val targetUserId: Int,
    @SerialName("expiry_hours") val expiryHours: Int = 24
)

@Serializable
data class TransferQRResponse(
    val message: String,
    @SerialName("vehicle_vin") val vehicleVin: String,
    val token: String,
    @SerialName("expires_at") val expiresAt: Long,
    @SerialName("qr_data")
    val qrData: QRData,
    @SerialName("otp_code") val otpCode: String? = null,
    @SerialName("target_user_id") val targetUserId: Int? = null,
    /** Solo el nombre de pila del destinatario (nunca apellidos ni email). */
    @SerialName("target_user_first_name") val targetUserFirstName: String? = null,
    /**
     * true cuando el backend devolvió una transferencia ya activa (409 en el POST o el GET
     * de la transferencia en curso) en lugar de crear una nueva. No viene en el JSON.
     */
    @Transient val alreadyActive: Boolean = false
)

/** Cuerpo del 409 de `POST api/vehicles/{vin}/transfer-qr/`: ya hay una transferencia pendiente. */
@Serializable
data class TransferConflictResponse(
    val error: String? = null,
    val transfer: TransferQRResponse? = null
)

/** Respuesta de `GET api/transfer/recipient/{user_id}/`. Solo expone el nombre de pila. */
@Serializable
data class TransferRecipient(
    val id: Int,
    @SerialName("first_name") val firstName: String
)

@Serializable
data class QRData(
    val vin: String,
    val token: String,
    @SerialName("target_user_id") val targetUserId: Int,
    @SerialName("expires_at") val expiresAt: Long,
    // Mismos nombres que `qr_data` del backend (vin, token, otp_code, target_user_id, expires_at).
    // Va al final por compatibilidad posicional y es nullable para leer QRs anteriores al OTP corto.
    @SerialName("otp_code") val otpCode: String? = null
) {
    companion object {
        // Tolerante a claves nuevas: un QR con campos extra no debe dejar de escanearse.
        private val parser = Json { ignoreUnknownKeys = true }

        fun fromJson(json: String): QRData? {
            return try {
                parser.decodeFromString(serializer(), json.trim())
            } catch (e: Exception) {
                null
            }
        }

        fun toJson(data: QRData): String {
            return Json.encodeToString(serializer(), data)
        }
    }
}

@Serializable
data class AcceptTransferResponse(
    val message: String,
    @SerialName("vehicle_vin") val vehicleVin: String,
    @SerialName("previous_owner_id") val previousOwnerId: Int,
    @SerialName("new_owner_id") val newOwnerId: Int,
    @SerialName("transferred_at") val transferredAt: Long
)

@Serializable
data class CancelTransferResponse(
    val message: String
)

@Serializable
data class TransferStatusResponse(
    val status: String, // "pending", "accepted", "cancelled", "expired"
    val message: String? = null,
    @SerialName("expires_at") val expiresAt: Long? = null
) {
    fun isAccepted(): Boolean = status == "accepted"
    fun isPending(): Boolean = status == "pending"
    fun isCancelled(): Boolean = status == "cancelled"
    fun isExpired(): Boolean = status == "expired"
}

@Serializable
data class PendingTransferItem(
    @SerialName("vehicle_vin") val vehicleVin: String,
    val plate: String,
    val brand: String,
    val model: String,
    @SerialName("source_user_id") val sourceUserId: Int,
    @SerialName("source_user_name") val sourceUserName: String,
    @SerialName("expires_at") val expiresAt: Long,
    @SerialName("created_at") val createdAt: Long
) {
    fun getVehicleDisplayName(): String = "$brand $model ($plate)"
    
    // expires_at llega en milisegundos (como en todo el backend), no en segundos.
    fun getTimeRemainingSeconds(): Long {
        val now = DatetimeUtils.currentTimeMillis()
        return if (expiresAt > now) (expiresAt - now) / 1000 else 0L
    }

    fun isExpired(): Boolean = DatetimeUtils.currentTimeMillis() >= expiresAt
    
    fun getFormattedTimeRemaining(): String {
        val seconds = getTimeRemainingSeconds()
        return when {
            seconds < 60 -> "Menos de 1 minuto"
            seconds < 3600 -> "${seconds / 60} minutos"
            seconds < 86400 -> "${seconds / 3600} horas"
            else -> "${seconds / 86400} dias"
        }
    }
}

@Serializable
data class PendingTransferResponse(
    val transfers: List<PendingTransferItem>
)

@Serializable
data class TransferVehicleRequest(
    @SerialName("target_user_id") val targetUserId: Int,
    @SerialName("transfer_is_primary") val transferIsPrimary: Boolean = true
)
